import { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Modal, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ItemImage } from '@components/CatalogElements';
import { AllocationService } from '@utility/services';
import { ToastMessage } from '@utility/toast';
import type { WaveAllocation } from 'types/wave/detail';

const { Text } = Typography;

type CancelAllocationsModalProps = {
  open: boolean;
  onClose: () => void;
  allocations: WaveAllocation[];
  preselectedIds: string[];
  onSuccess: () => void;
  description?: string;
};

export function CancelAllocationsModal({
  open,
  onClose,
  allocations,
  preselectedIds,
  onSuccess,
  description = 'These allocations will be cancelled and removed from the wave.',
}: CancelAllocationsModalProps) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedRowKeys(preselectedIds);
    }
  }, [open, preselectedIds]);

  const columns: ColumnsType<WaveAllocation> = useMemo(
    () => [
      {
        title: 'Product',
        key: 'product',
        render: (_, item) => {
          const product = item.inventory?.product;

          return (
            <Flex align="center" gap={12}>
              <ItemImage
                imageKey="images"
                displayImageKey="display_image"
                item={product}
                size="mini"
              />
              <Flex vertical>
                <Text>{product?.name}</Text>
                <Text type="secondary">SKU {product?.sku}</Text>
              </Flex>
            </Flex>
          );
        },
      },
      {
        title: 'Order',
        key: 'order',
        render: (_, item) => item.order?.displayId,
      },
      {
        title: 'Qty',
        key: 'quantity',
        width: 80,
        render: (_, item) => `${item.quantity} Unit(s)`,
      },
      {
        title: 'Location',
        key: 'location',
        render: (_, item) => (
          <Tag color={item.inventory?.zone?.color}>{item.inventory?.location?.code}</Tag>
        ),
      },
    ],
    [],
  );

  const handleConfirm = async () => {
    if (!selectedRowKeys.length) {
      ToastMessage.error('Please select at least one allocation');
      return;
    }

    try {
      setProcessing(true);
      await AllocationService.cancelAllocations(selectedRowKeys);
      ToastMessage.success('Allocations cancelled successfully');
      onClose();
      onSuccess();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Cancel ${selectedRowKeys.length} Allocation${selectedRowKeys.length === 1 ? '' : 's'}`}
      onCancel={onClose}
      width={760}
      centered
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose} disabled={processing}>
            Close
          </Button>
          <Button
            type="primary"
            danger
            loading={processing}
            disabled={!selectedRowKeys.length}
            onClick={handleConfirm}
          >
            Cancel Allocations
          </Button>
        </Flex>
      }
    >
      <Text type="secondary">{description}</Text>
      <Table
        rowKey="id"
        style={{ marginTop: 12 }}
        columns={columns}
        dataSource={allocations}
        pagination={false}
        size="small"
        scroll={{ y: 360 }}
        rowSelection={{
          selectedRowKeys,
          onChange: keys => setSelectedRowKeys(keys as string[]),
        }}
      />
    </Modal>
  );
}
